import { borderRadius, Button, color, Icon, space, typography } from '@primer-io/goat'
import { AnimatePresence, motion } from 'framer-motion'
import usePrimaryMenuItems from 'hooks/usePrimaryMenuItems'
import useSubMenuItems from 'hooks/useSubMenuItems'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import useFirstRender from '../../hooks/useFirstRender'
import { useSdkItems } from '../../hooks/useSdkItems'
import { defaultSpring } from '../../utils/transitions'
import { PrimaryMenuItems } from './PrimaryMenu'
import { SDK } from './sdk'
import { SecondaryMenuItems } from './SecondaryMenu'
import { ArrowUpRight, ChevronLeft } from '@primer-io/goat-icons'

const MobileMenuInner = styled.div`
	display: flex;
	flex-direction: column;
	align-content: stretch;
	min-height: 100%;
`
const MobileMenuBody = styled.div`
	flex-grow: 1;
	position: relative;
	overflow-x: hidden;
`
const MobileMenuFooter = styled.div`
	border-top: 1px solid ${color('gray-100')};

	display: flex;
	align-items: center;
	background: white;
	padding: 12px 24px;
	justify-content: flex-end;

	& > * + * {
		margin-left: 12px;
	}
`

const Panel = styled(motion.div)`
	position: absolute;
	top: 0px;
	right: 0px;
	left: 0px;
	min-height: 100%;

	padding-top: 24px;
	background: white;
`

const SecondaryMenuHeader = styled.button`
	position: relative;
	display: flex;
	align-items: center;

	padding: 8px 32px 8px 16px;
	margin-bottom: 16px;
	width: 100%;
	text-align: inherit;

	&:before {
		position: absolute;
		z-index: -1;
		content: '';
		top: 0px;
		right: 16px;
		bottom: 0px;
		left: 12px;
		background-color: transparent;
		border-radius: ${borderRadius('medium')};
	}

	&:hover {
		&:before {
			background-color: ${color('gray-100')};
		}
	}
`

const SecondaryMenuBack = styled.div`
	margin-right: 8px;
`

const SecondaryMenuTitle = styled.div`
	${typography('title-large')}
`

const SecondaryMenuIconContainer = styled.div`
	position: relative;
	display: grid;
	place-items: center;
	flex: none;
	width: 28px;
	height: 28px;
	margin-left: auto;

	background: white;
	border-radius: ${borderRadius('small')};
`

const SecondaryMenuItemsContainer = styled.div`
	padding: 0 ${space(5.5)};
`

const SecondaryMenuView = ({ location, menuItems, onBack, onClose, platform, primaryMenuItem, sdkItems, version }) => (
	<>
		<SecondaryMenuHeader onClick={onBack}>
			<SecondaryMenuBack>
				<Icon as={ChevronLeft} />
			</SecondaryMenuBack>
			<SecondaryMenuTitle>{primaryMenuItem?.title}</SecondaryMenuTitle>
			<SecondaryMenuIconContainer>
				<Icon as={primaryMenuItem?.icon} />
			</SecondaryMenuIconContainer>
		</SecondaryMenuHeader>

		<SecondaryMenuItemsContainer>
			{sdkItems && <SDK platforms={sdkItems} platform={platform} version={version} style={{ marginTop: -24 }} />}
			<SecondaryMenuItems
				location={location}
				menuItems={menuItems}
				onClick={(e, item) => {
					if (item.items?.length) {
						e.preventDefault()
						return
					}
					onClose?.()
				}}
			/>
		</SecondaryMenuItemsContainer>
	</>
)

const Footer = () => (
	<MobileMenuFooter>
		<Button variant='subtle' as='a' href='https://primer.io/docs/api' target='_blank'>
			API Guide
			<Button.Icon as={ArrowUpRight} />
		</Button>

		<Button as='a' href='https://sandbox-dashboard.primer.io' target='_blank'>
			Dashboard
		</Button>
	</MobileMenuFooter>
)

export default function MobileMenu({ location, onClose }) {
	const firstRender = useFirstRender()
	const { defaultPlatform, sdkItems: sdkPlatforms } = useSdkItems()
	const { id, latest } = sdkPlatforms[defaultPlatform]

	const { primaryMenuItems, currentPrimaryMenuItem } = usePrimaryMenuItems(location)
	const [selectedPrimaryMenuItem, setSelectedPrimaryMenuItem] = useState(currentPrimaryMenuItem)

	const { platform = id, secondaryMenuItems: currentSecondaryMenuItems, version = latest } = useSubMenuItems(location)
	const { sdkItems, secondaryMenuItems } = useSubMenuItems({
		pathname: selectedPrimaryMenuItem?.url ?? '/',
		hash: '',
	})

	useEffect(() => {
		setSelectedPrimaryMenuItem(currentPrimaryMenuItem)
	}, [location.pathname])

	const items = sdkItems?.[platform]?.versions[version] || secondaryMenuItems

	return (
		<MobileMenuInner>
			<MobileMenuBody>
				<AnimatePresence>
					{!items?.length && (
						<Panel
							key='Primary'
							style={{
								zIndex: 1,
								boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.2)',
							}}
							initial={!firstRender && { x: '-100%', width: '80%', overflow: 'hidden' }}
							animate={{ x: '0%', width: '100%', overflow: 'inherit' }}
							exit={{ x: '-100%', width: '80%', overflow: 'hidden' }}
							transition={defaultSpring}
						>
							<PrimaryMenuItems
								currentMenuItemUrl={currentPrimaryMenuItem?.url}
								isOpen
								menuItems={primaryMenuItems}
								showChevronRight
								onClick={(e, menuItem, subMenuItems) => {
									if (subMenuItems?.length) {
										e.preventDefault()
										setSelectedPrimaryMenuItem(menuItem)
										return
									}
									onClose?.()
								}}
							/>
						</Panel>
					)}

					{items?.length && (
						<Panel
							key='Secondary'
							style={{ zIndex: 0 }}
							initial={!firstRender && { x: 100 }}
							animate={{ x: 0 }}
							exit={{ x: 100 }}
							transition={defaultSpring}
						>
							<SecondaryMenuView
								location={location}
								menuItems={
									selectedPrimaryMenuItem.url === currentPrimaryMenuItem.url ? currentSecondaryMenuItems : items
								}
								primaryMenuItem={selectedPrimaryMenuItem}
								platform={platform}
								version={version}
								sdkItems={sdkItems}
								onBack={() => setSelectedPrimaryMenuItem(null)}
								onClose={onClose}
							/>
						</Panel>
					)}
				</AnimatePresence>
			</MobileMenuBody>
			<Footer />
		</MobileMenuInner>
	)
}
